import { useQuery } from "@tanstack/react-query";
import { request } from "./client";
import { baseUrl } from "./services";
import { isMockMode } from "./mock/mode";
import { freshToken } from "../auth/refresh";

/** Pallet rejasi (picking-service, TZ 3.0 OPT-03…05). Hammasi mm va gramm. */
export interface PalletLimits {
  length_mm: number;
  width_mm: number;
  max_height_mm: number;
  base_height_mm: number;
  max_weight_g: number;
}

export interface PalletBox {
  key: string;
  code: string;
  x_mm: number;
  y_mm: number;
  z_mm: number;
  length_mm: number;
  width_mm: number;
  height_mm: number;
  weight_g: number;
  rotated: boolean;
  top_only: boolean;
}

export interface PalletLayer {
  index: number;
  z_mm: number;
  height_mm: number;
  weight_g: number;
  top_only: boolean;
  boxes: PalletBox[];
}

export interface Pallet {
  index: number;
  height_mm: number;
  weight_g: number;
  boxes: number;
  layers: PalletLayer[];
  skus: Array<{ key: string; code: string; name: string; boxes: number }>;
}

export interface PalletLegend {
  key: string;
  code: string;
  name: string;
  color: string;
  boxes: number;
  units_per_box?: string;
  length_mm: number;
  width_mm: number;
  height_mm: number;
  weight_g: number;
  fragile: boolean;
  no_stack: boolean;
}

export interface PalletPlan {
  limits: PalletLimits;
  legend: PalletLegend[];
  pallets: Pallet[];
  reliable: boolean;
  total_boxes: number;
  missing: Array<{ key: string; code: string; name: string; boxes: number; fields: string[] }>;
  unplaced: Array<{ key: string; code: string; name: string; boxes: number; reason: string }>;
}

export interface PalletPlanResponse {
  order_id: string;
  order_number: string;
  customer_name?: string;
  status: string;
  plan: PalletPlan;
}

export interface PickingOrderRow {
  id: string;
  order_number: string;
  // Buyurtma ro'yxati faqat IDni beradi — nom master-data'dan olinadi.
  customer_id?: string;
  customer_name?: string;
  status: string;
  created_at: string;
}

export function usePalletPlan(orderId: string | undefined, maxHeightCm: string) {
  return useQuery({
    queryKey: ["picking", "pallet-plan", orderId, maxHeightCm],
    enabled: Boolean(orderId),
    queryFn: ({ signal }) =>
      request<PalletPlanResponse>("picking", `/api/v1/picking/orders/${orderId}/pallet-plan`, {
        query: { max_height_cm: maxHeightCm || undefined },
        signal,
      }),
  });
}

/**
 * Reja PDF i (OPT-04). Fayl yangi oynada ochiladi — tokenli so'rov
 * oddiy havola bilan yuborib bo'lmaydi.
 */
export async function openPalletPlanPdf(orderId: string, maxHeightCm: string): Promise<void> {
  if (isMockMode()) {
    throw new Error("Namuna rejimida PDF yaratilmaydi");
  }
  const url = new URL(`${baseUrl("picking")}/api/v1/picking/orders/${orderId}/pallet-plan.pdf`);
  if (maxHeightCm) url.searchParams.set("max_height_cm", maxHeightCm);
  const token = await freshToken();
  const response = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error(`PDF: HTTP ${response.status}`);
  const blob = await response.blob();
  const link = URL.createObjectURL(blob);
  window.open(link, "_blank", "noopener");
  window.setTimeout(() => URL.revokeObjectURL(link), 60_000);
}
